import React from "react";
import BreadcrumbsWrapper from "@components/BreadcrumbsWrapper";

/**
 * CollectionSlimBanner - A banner component for collection pages
 * @param {Object} props
 * @param {Array} props.breadcrumbs - Array of breadcrumb objects
 * @param {Object} props.heroData - Hero/banner data including images
 * @param {boolean} props.showBreadcrumbs - Whether to show breadcrumbs
 * @param {boolean} props.hasBanner - Whether to show banner image
 * @param {string} props.collectionTitle - Title of the collection
 */
const CollectionSlimBanner = ({ 
    breadcrumbs = [], 
    heroData = {}, 
    showBreadcrumbs = true, 
    hasBanner = false, 
    isDisplay = true,
    collectionTitle = '' 
}) => {
    const bgColor = heroData?.bgColor || 'white';
    const fullWidthClass = heroData?.isFullWidth ? '' : 'wrap-xl mx-auto';

    // Transform breadcrumbs data to match BreadcrumbsWrapper expected format
    const transformedBreadcrumbs = breadcrumbs?.map((crumb, index, array) => {
        // Handle the structure from existing codebase: { name, link }
        if (crumb.name && crumb.link !== undefined) {
            return {
                label: crumb.name,
                href: crumb.link,
                isActive: index === array.length - 1 // Last item is active
            };
        }
        // Handle the structure from constants: { label, href, isActive/isCurrentPage }
        else if (crumb.label && crumb.href !== undefined) {
            return {
                label: crumb.label,
                href: crumb.href,
                isActive: crumb.isActive || crumb.isCurrentPage || index === array.length - 1
            };
        }
        // Fallback for any other structure
        return {
            label: crumb.label || crumb.name || '',
            href: crumb.href || crumb.link || '#',
            isActive: crumb.isActive || crumb.isCurrentPage || index === array.length - 1
        };
    }) || [];

    if (!isDisplay) {
        return null
    }

    if (!hasBanner) {
        // No banner version - just content on white background (similar to Hero.jsx)
        return (
            <div className="relative">
                <div className="bg-background-primary-inverse border-b border-border-tertiary-subtle">
                    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
                        <div className="flex flex-col items-start gap-2">
                            {/* Breadcrumbs */}
                            {showBreadcrumbs && (
                                <BreadcrumbsWrapper breadcrumbsData={transformedBreadcrumbs} />
                            )}
                            
                            {/* Title */}
                            {collectionTitle && (
                                <h1 className="md:text-web-h3-heading-3 text-mobile-h3-heading-3 font-styrene text-text-primary capitalize">
                                    {collectionTitle}
                                </h1>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // With banner version
    return (
        <div
            className="hero-banner relative w-full collection-slim-banner border-b !border-border-tertiary-subtle"
            style={heroData?.isFullWidth
                ? { margin: 0, padding: 0, position: 'relative', left: '50%', transform: 'translateX(-50%)', width: '100vw' }
                : {}
            }
        >
            {/* Always show breadcrumbs when we have a banner, overlaid on the image like Hero.jsx */}
            {showBreadcrumbs && (
                <div className="absolute left-0 !bottom-0 md:w-[496px] w-[348px] bg-background-primary-inverse pt-6 pe-4 pb-4 ps-6 md:p-10 flex flex-col gap-5 z-10">
                    <div className="flex flex-col items-start gap-2">
                        {/* Breadcrumbs */}
                        <BreadcrumbsWrapper breadcrumbsData={transformedBreadcrumbs} />
                        
                        {/* Title */}
                        {collectionTitle && (
                            <h1 className="md:text-web-h3-heading-3 text-mobile-h3-heading-3 font-styrene text-text-primary capitalize">
                                {collectionTitle}
                            </h1>
                        )}
                    </div>
                </div>
            )}
            
            <div
                className={`hero-banner-inner ${fullWidthClass}`}
                style={heroData?.isFullWidth
                    ? { margin: 0, padding: 0, maxWidth: 'none', width: '100vw', position: 'relative', left: '50%', transform: 'translateX(-50%)' }
                    : {}
                }
            >
                <div className="hero-image relative flex items-center justify-center lg:min-h-fit">
                    <div className="hero-image-mobile flex md:hidden w-full">
                        <img
                            className="object-cover w-full"
                            src={heroData.mobileImage}
                            alt={heroData.altText}
                        />
                    </div>
                    <div
                        className={`hero-image-desktop hidden md:flex ${heroData?.isFullWidth ? 'w-full' : ''}`}
                    >
                        <img
                            src={heroData.desktopImage}
                            alt={heroData.altText}
                            className={heroData?.isFullWidth ? 'w-full' : ''}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CollectionSlimBanner;
